/**
 * ContentSelectionTable - Table displaying content items with checkbox selection
 *
 * Features:
 * - Row selection via checkboxes
 * - Extended click area on checkbox column (like ClusterResultsPage)
 * - Status badges for Article and FAQ schema
 * - Pagination (25 items per page)
 * - Columns: Checkbox, Title, Article Status, FAQ Status, Modified Date
 */

import { Table, Tag, Typography } from "antd";
import type { ColumnsType, TableProps } from "antd/es/table";
import { AlertCircle, CheckCircle } from "lucide-react";
import type React from "react";
import { useMemo, useCallback } from "react";
import type { ContentItem, FAQContentType } from "@/types/schema";

const { Text } = Typography;

interface ContentSelectionTableProps {
	schemaType: "article" | "faq";
	contentType?: FAQContentType;
	selectedIds: number[];
	onSelectionChange: (ids: number[]) => void;
	filters: {
		search: string;
		schemaStatus?: string;
		contentType?: string;
	};
	items: ContentItem[];
	loading: boolean;
}

export const ContentSelectionTable: React.FC<ContentSelectionTableProps> = ({
	schemaType,
	contentType,
	selectedIds,
	onSelectionChange,
	filters,
	items,
	loading,
}) => {
	// Toggle individual row selection (used by extended checkbox click area)
	const toggleRowSelection = useCallback(
		(itemId: number, selected: boolean) => {
			if (selected) {
				onSelectionChange([...selectedIds, itemId]);
			} else {
				onSelectionChange(selectedIds.filter((id) => id !== itemId));
			}
		},
		[selectedIds, onSelectionChange],
	);

	const columns: ColumnsType<ContentItem> = [
		{
			title: "Title",
			dataIndex: "title",
			key: "title",
			width: 300,
			ellipsis: true,
			render: (title: string, record) => (
				<div>
					<Text strong style={{ display: "block" }}>
						{title}
					</Text>
					<Text type="secondary" style={{ fontSize: 11 }}>
						{record.slug}
					</Text>
				</div>
			),
		},
		{
			title: "Article Status",
			dataIndex: "has_article_schema",
			key: "article_status",
			width: 120,
			align: "center",
			render: (hasSchema: boolean) =>
				hasSchema ? (
					<Tag icon={<CheckCircle size={12} />} color="success">
						Has Schema
					</Tag>
				) : (
					<Tag icon={<AlertCircle size={12} />} color="warning">
						Needs Schema
					</Tag>
				),
		},
		{
			title: "FAQ Status",
			dataIndex: "has_faq_schema",
			key: "faq_status",
			width: 120,
			align: "center",
			render: (hasSchema: boolean) =>
				hasSchema ? (
					<Tag icon={<CheckCircle size={12} />} color="success">
						Has Schema
					</Tag>
				) : (
					<Tag icon={<AlertCircle size={12} />} color="warning">
						Needs Schema
					</Tag>
				),
		},
		{
			title: "Modified",
			dataIndex: "updated_at",
			key: "updated_at",
			width: 100,
			render: (date: string) => {
				const d = new Date(date);
				return (
					<Text type="secondary" style={{ fontSize: 12 }}>
						{d.toLocaleDateString()}
					</Text>
				);
			},
		},
	];

	// Row selection with extended click area - clicking anywhere in checkbox cell toggles selection
	const rowSelection: TableProps<ContentItem>["rowSelection"] = useMemo(
		() => ({
			selectedRowKeys: selectedIds,
			onChange: (keys) => onSelectionChange(keys as number[]),
			columnWidth: 48,
			onCell: (record: ContentItem) => ({
				onClick: (e: React.MouseEvent) => {
					e.stopPropagation();
					const isSelected = selectedIds.includes(record.id);
					toggleRowSelection(record.id, !isSelected);
				},
				style: { cursor: "pointer" },
			}),
		}),
		[selectedIds, onSelectionChange, toggleRowSelection],
	);

	return (
		<div>
			<Table
				columns={columns}
				dataSource={items}
				rowKey="id"
				rowSelection={rowSelection}
				loading={loading}
				pagination={{
					pageSize: 25,
					showSizeChanger: false,
					showTotal: (total) => `${total} items`,
				}}
				scroll={{ x: 700 }}
				locale={{
					emptyText: loading
						? "Loading content..."
						: "No content items found. Adjust your filters or check your content library.",
				}}
			/>
		</div>
	);
};

export default ContentSelectionTable;
